<template>
  <div class="checkout-container">
    <div class="checkout-container-left">
      <div
        v-if="!!invoice && invoice.invoice_items"
        class="invoice-items-container"
      >
        <div
          v-for="invoiceItem in invoice.invoice_items"
          v-show="!!invoiceItem.inventory_id"
          :key="invoiceItem.invoice_item_id"
          class="invoice-item"
        >
          <img
            class="invoice-item-thumbnail"
            :src="invoiceItem.image"
            @click="goToInventoryItem(invoiceItem.url)"
          >
          <p v-html="invoiceItem.description" />
          <label>{{
            invoiceItem.display_hammer_price
              ? invoiceItem.display_hammer_price.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })
              : '-'
          }}</label>
        </div>
      </div>

      <!-- Auction Items Total Bottom Section -->
      <div class="auction-item-total-container">
        <div>
          <label class="title">Lot Hammer</label>
          <label class="dollar-value">${{
            invoiceHammerTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
        <div>
          <label class="title">Buyer's Premium</label>
          <label class="dollar-value">${{
            buyersPremiumTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
        <div v-if="invoiceShippingTotal > 0">
          <label class="title">Shipping</label>
          <label class="dollar-value">${{
            invoiceShippingTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
        <div>
          <label class="title">Auction Items Total</label>
          <label class="dollar-value">${{
            itemsTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          }}</label>
        </div>
        <div>
          <p>*Total does not include applicable sales tax</p>
        </div>
      </div>
    </div>
    <div class="checkout-container-right">
      <!-- Top Header -->
      <div class="top-header-container">
        <img
          class="logo largeLogo"
          src="/images/dark-logo.svg"
          alt="Leland Little Auctions"
          @click="exit"
        >
        <label>AUCTION ITEM CHECKOUT</label>
      </div>

      <div class="details-container">
        <div class="breadcrumb-container">
          <div
            v-for="(crumb, index) in breadCrumbs"
            :key="crumb.title"
            class="breadcrumb-item"
          >
            <p
              v-if="!crumb.hidden"
              :class="{
                'breadcrumb-item-active': crumb.active,
                'breadcrumb-item-clickable': !crumb.active && crumb.canNavigate,
                'breadcrumb-item-unclickable':
                  !crumb.active && !crumb.canNavigate
              }"
            >
              {{ crumb.title }}
            </p>
            <span v-if="index != breadCrumbs.length - 1 && !crumb.hidden">
              /
            </span>
          </div>
        </div>

        <div class="details-inner-container checkout-form-container">
          <h1>Congratulations!</h1>
          <h2 class="customer-name">
            {{ customerName }}
          </h2>

          <p v-if="numInvoiceInventoryItems > 0">
            You have won {{ numInvoiceInventoryItems }} Auction Item<span v-if="numInvoiceInventoryItems != 1">s</span>
          </p>
          <p v-if="paidInFull === false && propertyOnsite === true">
            Please click Continue to proceed to payment and collection
            management.
          </p>
          <p v-else-if="paidInFull === true && propertyOnsite === true">
            Please click Continue to proceed to collection
            management.
          </p>

          <button
            class="continue-btn"
            @click="goToShipping()"
          >
            Continue
          </button>
        </div>
      </div>

      <!-- Bottom Footer -->
      <div class="bottom-footer-container">
        <p
          v-if="isStaffView"
          class="staff-view-banner"
        >
          STAFF VIEW: Viewing invoice for {{ customerName }}
        </p>
        <div class="bottom-disclaimer">
          If you have any questions, please contact us here:
          <a href="mailto:info@lelandlittle.com">info@lelandlittle.com </a>
        </div>
        <button @click="exit()">
          Exit
        </button>
      </div>
    </div>

    <LoadingOverlayComponent
      v-if="showLoading"
      :title="loadingTitle"
    />

    <AlertConfirmComponent
      v-if="showCustomQuoteAlert"
      :title="customQuoteAlertTitle"
      :submit-text="submitText"
      :cancel-text="cancelText"
      :message="customQuoteAlertMessage"
      @close="cancelCustomQuote()"
      @submit="exit()"
    />

    <AlertComponent
      v-if="showAlert"
      :title="alertTitle"
      :message="alertMessage"
      :secondary-action="alertSecondaryAction"
      @close="exit()"
    />
  </div>
</template>

<script>
import LoadingOverlayComponent from '@/components/Shared/LoadingOverlayComponent.vue'
import AlertComponent from '@/components/Shared/AlertComponent.vue'
import apiMixin from '@/mixins/apiMixin.js'
import InvoiceValidateMixin from '@/mixins/InvoiceValidateMixin.js'
import { isEqual } from 'lodash'
import AlertConfirmComponent from '@/components/Shared/AlertConfirmComponent.vue'

/**
 * The Aucction Items Checkout component.
 * TODO: refactor, clean up, and document how it works
 * @displayName Auction Items Component
 */
export default {
	name: 'AuctionItemsComponent',
	components: {
		LoadingOverlayComponent,
		AlertComponent,
		AlertConfirmComponent
	},
	mixins: [apiMixin, InvoiceValidateMixin],
	props: ['invoice_id'],
	data () {
		return {
			loadingTitle: 'Gathering your invoice details',
			loadingSubTitle: '',
			retryCount: 0,
			showAlert: false,
			shippingAddressUpdated: false,
			alertTitle: '',
			alertMessage:
				'If you have any questions, please contact us here: info@lelandlittle.com',
			submitText: 'OK',
			cancelText: 'Cancel Custom Quote',
			alertSecondaryAction: null,
			customQuoteIsQuoted: false,
			skipToPayment: false,
			useArta: true,
			showCustomQuoteAlert: false,
			customQuoteAlertTitle: '',
			customQuoteAlertMessage: ''
		}
	},
	/**
	 * Computed properties
	 * @public
	 */
	computed: {
		/**
		 * TODO: This doesn't get called?
		 * @public
		 */
		hasCustomQuote: function () {
			let quoteCandidates = this.$store.getters.quoteCandidates
			if (!quoteCandidates.length) {
				return false
			}
			if (quoteCandidates[0].isCustom) {
				return true
			}
			return false
		},
		/**
		 * Determines if LoadingOverlayComponent displays
		 * @public
		 */
		showLoading: function () {
			return (
				typeof this.apiResponse.get_customer_invoice === 'undefined' ||
				typeof this.apiPending.arta_get_quote_request !== 'undefined' ||
				typeof this.apiPending.cancel_custom_quote !== 'undefined'
			)
		},
		/**
		 * The apiPending object
		 * @public
		 */
		apiPending: function () {
			return this.$store.getters.apiPending
		},
		/**
		 * The apiResponse object
		 * @public
		 */
		apiResponse: function () {
			return this.$store.getters.apiResponse
		},
		/**
		 * The customer name on invoice
		 * @public
		 */
		customerName: function () {
			return this.invoice ? this.invoice.customer_display_name : ''
			// return this.$store.getters.customer.display_name
		},
		/**
		 * Assert if authenticated user is LL staff and invoice is not their own.
		 *
		 * If invoice customer id is not equal to logged in customer and logged in
		 * customer is staff. (api will not return invoice unless authenticated
		 * customer id is same as invoice id OR authenticated customer is staff)
		 * @public
		 */
		isStaffView: function () {
			if (
				this.invoice &&
				this.invoice.customer_id &&
				this.$store.getters.customer.customer_id !== this.invoice.customer_id &&
				typeof this.apiResponse.get_customer_invoice !== 'undefined' &&
				typeof this.apiResponse.get_account !== 'undefined'
			) {
				return true
			}
			return false
		},
		/**
		 * The id of the current invoice
		 * @public
		 */
		currentInvoiceId: function () {
			return this.$store.getters.checkoutInvoiceId
		},
		/**
		 * The total cost of invoice items without tax
		 * @public
		 */
		itemsTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_total_no_tax) {
				return parseFloat(invoice.invoice_total_no_tax).toFixed(2)
			}

			return parseFloat(0).toFixed(2)
		},
		/**
		 * The invoice buyer's premium total
		 * @public
		 */
		buyersPremiumTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_buyers_total) {
				return invoice.invoice_buyers_total
			}

			return 0
		},
		/**
		 * The invoice hammer total
		 * @public
		 */
		invoiceHammerTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_hammer_total) {
				return invoice.invoice_hammer_total
			}

			return 0
		},
		/**
		 * The invoice shipping total
		 * @public
		 */
		invoiceShippingTotal: function () {
			const invoice = this.invoice
			if (invoice && invoice.invoice_shipping_total) {
				return invoice.invoice_shipping_total
			}

			return 0
		},
		/**
		 * All invoices for the authenticated user.
		 * @public
		 */
		invoices: function () {
			return this.$store.getters.invoices
		},
		/**
		 * The current invoice.
		 *
		 * @public
		 */
		invoice: function () {
			if (this.invoiceIsLoading) {
				return
			}
			const key = `INVOICEID_${this.currentInvoiceId}`
			const tempInvoice = this.invoices[key]

			// If tempInvoice is undefined and not still loading from API we return false to trigger the invoice watcher
			return typeof tempInvoice !== 'undefined' ? tempInvoice : false
		},
		/**
		 * @public
		 */
		invoiceIsLoading: function () {
			return this.$store.state.invoiceIsLoading
		},
		/**
		 * The total number of lines on the invoice
		 * @public
		 */
		numInvoiceItems: function () {
			const tempInvoice = this.invoice
			if (tempInvoice.length === 0) {
				return 0
			} else {
				return tempInvoice.invoice_items.length
			}
		},
		/**
		 * The breadcrumbs array object
		 * @public
		 */
		breadCrumbs: function () {
			return [
				{
					active: true,
					canNavigate: false,
					title: 'Auction Items',
					route: 'auctionItems'
				},
				{
					active: false,
					canNavigate: false,
					hidden: !this.useArta,
					title: 'Shipping',
					route: 'shipping'
				},
				{
					active: false,
					canNavigate: false,
					hidden: !this.useArta,
					title: 'Options',
					route: 'shippingOptions'
				},
				{
					active: false,
					canNavigate: false,
					title: 'Payment Info',
					route: 'paymentInfo'
				}
			]
		},
		/**
		 * The number of invoice items that have inventory id greater than 0
		 * @public
		 */
		numInvoiceInventoryItems: function () {
			let tempInvoice = this.invoice
			if (
				tempInvoice &&
				tempInvoice.invoice_items &&
				tempInvoice.invoice_items.length === 0
			) {
				return 0
			}
			let cnt = 0
			if (tempInvoice && tempInvoice.invoice_items) {
				for (let i = 0; i < tempInvoice.invoice_items.length; i++) {
					if (
						tempInvoice.invoice_items[i].inventory_id > 0
					) {
						cnt++
					}
				}
			}
			return cnt
		},
		/**
		 * The inventory ids on the invoice
		 * @public
		 */
		inventory_ids: function () {
			let tempInvoice = this.invoice
			let out = []
			if (typeof tempInvoice.invoice_items === 'undefined') {
				return out
			}
			for (let i = 0; i < tempInvoice.invoice_items.length; i++) {
				let item = tempInvoice.invoice_items[i]
				if (item.inventory_id && !item.refund_inventory_id) {
					out.push(item.inventory_id)
				}
			}
			return out
		},
        /**
         * Assert if invoice paid in full
         * @public
         */
        paidInFull: function () {
            return this.invoice && this.invoice.paid_in_full === 'y'
        },
        /**
         * Assert if invoice has property onsite
         * @public
         */
        propertyOnsite: function () {
            let propertyOnsite = false;
            if (this.invoice && this.invoice.invoice_items) {
                propertyOnsite = this.invoice.invoice_items.some(item => item.has_left_building !== 'y' && item.inventory_id > 0);
            }
            return propertyOnsite;
        }
	},
	watch: {
		invoice: function (invoice, prevInvoice) {
			const isLoaded = !this.invoiceIsLoading
			if (!isEqual(prevInvoice, invoice)) {
				// Invoice changed, validate it
				if (isLoaded) {
					this.validateInvoice(invoice)
				}
			}
		}
	},
	/**
	 * Runs when component is mounted
	 * @public
	 */
	mounted () {
		// If you navigate to Payment Info, and then back to Auction Items, the mixin doesn't re-run, so skipToPayment and useArta are not set
		if (this.invoice !== undefined) {
			this.validateInvoice(this.invoice)
		}
	},
	methods: {
		/**
		 * Gets called by goToShipping when user clicks Continue button to go to shipping
		 * @public
		 */
		getQuoteRequest () {
			// this is a kludge to keep from locking the user out of changing their quote if they just click back to the front page.
			// If the user has already entered data on this page load, bail.
			let quoteCandidates = this.$store.getters.quoteCandidates
			if (quoteCandidates.length) {
				this.shippingAddressUpdated = true
				return Promise.resolve(true)
			}

			var apiPayload = {
				apiEndpoint: 'shipping',
				postData: {
					action: 'arta_get_quote_request',
					inventory_ids: this.inventory_ids
				}
			}

			let promise = this.$store
				.dispatch('apiCall', apiPayload)
				.then(response => {
					// Set address mapping constants
					const addressMapping = {
						address_line_1: 'street_1',
						address_line_2: 'street_2',
						address_line_3: 'street_3',
						city: 'city',
						region: 'state',
						country: 'country',
						postal_code: 'zip'
					}
					const contactMapping = {
						name: 'name',
						email_address: 'email_address',
						phone_number: 'phone_number'
					}
					// Set new address object
					let newAddress = {
						contacts: {}
					}
					// Return if api call wasn not a success
					if (response.data.result !== 'success') {
						// todo: move shippingAddressUpdated back above this, just crashing here for testing
						return true
					}
					this.shippingAddressUpdated = true

					// no pre-existing quote request. cool.
					if (!response.data.payload.found) {
						// We must set the shipping address to that customer billing address on the invoice.
						newAddress.street_1 = this.invoice.customer_billing_street_address
						newAddress.city = this.invoice.customer_billing_city
						newAddress.state = this.invoice.customer_billing_state
						newAddress.zip = this.invoice.customer_billing_zip
						newAddress.country = this.invoice.customer_billing_country
						newAddress.contacts.name = this.invoice.customer_display_name
						newAddress.contacts.email_address = this.invoice.customer_email
						newAddress.contacts.phone_number = this.invoice.customer_phone
						this.$store.commit('UPDATE_SHIPPING_ADDRESS', newAddress)
						return true
					}

					// get the quotes out of the pre-existing quote request
					let artaResponse = response.data.payload.response
					let quotes = []
					for (let i = 0; i < artaResponse.quotes.length; i++) {
						let artaQuote = artaResponse.quotes[i]
						let quote = {
							id: artaQuote.id,
							description: artaQuote._display_name,
							price: artaQuote._surcharged_total,
							selected: false,
							// TODO: we don't actually know this. Err on the side of "yes" for now to make sure customers can't overwrite their custom quotes
							isCustom: true,
							request: {
								id: artaResponse.id
							}
						}
						quotes.push(quote)
					}
					this.$store.commit('UPDATE_QUOTE_CANDIDATES', quotes)

					// now pull the address and contacts out of the quote request
					let artaAddress = artaResponse.destination
					let artaContacts = artaAddress.contacts[0]
					let artaField = null
					let ourField = null
					for (artaField in addressMapping) {
						ourField = addressMapping[artaField]
						newAddress[ourField] = ''
						if (
							typeof artaAddress[artaField] === typeof 'asdf' &&
							artaAddress[artaField].length
						) {
							newAddress[ourField] = artaAddress[artaField]
						}
					}
					for (artaField in contactMapping) {
						ourField = contactMapping[artaField]
						newAddress.contacts[ourField] = ''
						if (
							typeof artaContacts[artaField] === typeof 'asdf' &&
							artaContacts[artaField].length
						) {
							newAddress.contacts[ourField] = artaContacts[artaField]
						}
					}
					this.$store.commit('UPDATE_SHIPPING_ADDRESS', newAddress)

					return true
				})
			return promise
		},
		/**
		 * Gets called when getQuoteRequest finishes
		 * @public
		 */
		reallyGoToShipping () {
			const tempInvoice = this.invoices['INVOICEID_' + this.currentInvoiceId]
			this.$store.dispatch('getAllCustomerShippingOrders')

			if (this.paidInFull && this.propertyOnsite) {
				this.$router.push({ name: 'shipping' })
			} else if (this.skipToPayment) {
				this.$router.push({ name: 'paymentInfo' })
			} else if (tempInvoice !== undefined && !this.canUseArta(tempInvoice)) {
				// InvoiceValidateMixin
				// This invoice cannot use Arta
				this.$router.push({ name: 'paymentInfo' })
			} else if (this.customQuoteIsQuoted) {
				this.$router.push({ name: 'shippingOptions' })
			} else {
				this.$router.push({ name: 'shipping' })
			}
		},
		/**
		 * Gets called when user clicks Continue button to go to shipping
		 * @public
		 */
		goToShipping () {
			return this.getQuoteRequest().then(result => {
				this.reallyGoToShipping()
			})
		},
		/**
		 * Gets called when user clicks Exit button in footer
		 * @public
		 */
		exit () {
			// apiMixin
			// Check for a exit-referrer in localstorage
			const referrer = localStorage.getItem('exit-referrer')
			if (referrer) {
				// If we have a exit-referrer, redirect to it
				window.location.replace(referrer)
				return
			}
			// Otherwise, redirect to the home page
			window.location.replace(this.getUrlHost(this.$store.getters.apiPath))
		},
		/**
		 * TODO: This doesn't get called?
		 * @public
		 */
		invoiceNeedsAttention (status) {
			this.$store.dispatch('setInvoiceNeedsAttention', {
				invoice_id: this.currentInvoiceId,
				attention: status
			})
		},
		/**
		 * Gets called when user clicks invoice item thumbnail image
		 * @public
		 */
		goToInventoryItem (url) {
			window.open(this.getUrlHost(this.$store.getters.apiPath) + url, '_blank')
		},
		cancelCustomQuote () {
			this.showAlert = false // hide the cancel custom quote alert

			var requestId = null
			var invoiceId = this.invoice.invoice_id
			var inventoryIds = []
			// Gather all the details we need to cancel this quote
			this.invoice.invoice_items.forEach(item => {
				if (item.arta_request_id !== null) {
					requestId = item.arta_request_id
				}

				// Only grab valid inventory items
				if (item.shipping_charge !== 'y' &&
					item.automobile_charge !== 'y' &&
					item.packing_charge !== 'y') {
					inventoryIds.push(item.inventory_id)
				}
			})

			// Make sure we were able to get what we need
			if (requestId === null || invoiceId === null || inventoryIds.length === 0) {
				this.customQuoteAlertTitle = 'We are unable to cancel this custom quote at this time.'
				this.customQuoteAlertMessage = 'If you have any questions, please contact us here: info@lelandlittle.com'
				this.showCustomQuoteAlert = true // show the error alert

				return
			}

			var cmp = this
			let apiPayload = {}
			apiPayload.apiEndpoint = 'shipping'
			apiPayload.postData = {}
			apiPayload.postData.action = 'cancel_custom_quote'
			apiPayload.postData.request_id = requestId
			apiPayload.postData.invoice_id = invoiceId
			apiPayload.postData.inventory_ids = inventoryIds
			this.loadingTitle = 'Canceling Custom Quote...'

			cmp.$store.dispatch('apiCall', apiPayload).then(response => {
				if (response.data.result !== 'success') {
					this.customQuoteAlertTitle = 'We are unable to cancel this custom quote at this time.'
					this.customQuoteAlertMessage = 'If you have any questions, please contact us here: info@lelandlittle.com'
					this.showCustomQuoteAlert = true
				} else {
					// reload the page
					window.location.reload()
				}
			})
		}
	}
}
</script>

<style scoped></style>
